// `Card` (design-system spec §3.6). White, 1 px border, radius 8, resting
// shadow. A Card must answer a question or contain an action: it has a title
// naming what the user came for, or at least one interactive element. Cards
// never nest; nothing is "emphasized" with color.
//
// `Card.Body` (rev-7, D-426) is the one padded body: 12 px top and bottom,
// 14 px at the sides, the same inset as the header above it. A screen puts its
// text, fields and empty states in it rather than padding its own wrapper, and
// a full-bleed child (a DataTable, a list whose rows carry their own padding)
// goes straight in the Card without it.

import type { ReactNode } from "react";

export interface CardProps {
  /** 13 px/600 header title. Omit for action-only cards (rare). */
  title?: ReactNode;
  /** Faint count beside the title — "· 9". */
  count?: number | string | undefined;
  /** Right-side header controls: filters, chips, one small action. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

function CardRoot({ title, count, action, children, className, ...rest }: CardProps) {
  const classes = ["ds-card"];
  if (className !== undefined) classes.push(className);
  return (
    <section className={classes.join(" ")} {...rest}>
      {title !== undefined || action !== undefined ? (
        <header className="ds-chead">
          {title !== undefined ? (
            <h3>
              {title}
              {count !== undefined ? (
                <>
                  {" "}
                  <span className="ds-chead-n num">· {count}</span>
                </>
              ) : null}
            </h3>
          ) : null}
          {action !== undefined ? <div className="ds-chead-right">{action}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export interface CardBodyProps {
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/** The padded body of a Card (rev-7). */
export function CardBody({ children, className, ...rest }: CardBodyProps) {
  return (
    <div className={className === undefined ? "ds-cbody" : `ds-cbody ${className}`} {...rest}>
      {children}
    </div>
  );
}

export const Card = Object.assign(CardRoot, { Body: CardBody });
